import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import JobSheet from "@/pages/JobSheet";
import LabelPrint from "@/pages/LabelPrint";
import SystemSettings from "@/pages/SystemSettings";
import ScanJob from "@/pages/ScanJob";
import BulkScan from "@/pages/BulkScan";
import Stock from "@/pages/Stock";
import OutsourceOrders from "@/pages/OutsourceOrders";
import Attendance from "@/pages/Attendance";
import Awards from "@/pages/Awards";
import StockScan from "@/pages/StockScan";
import StockLabels from "@/pages/StockLabels";
import StockBulkScan from "@/pages/StockBulkScan";
import NewOrder from "@/pages/NewOrder";
import ProductionBoard from "@/pages/ProductionBoard";
import Customers from "@/pages/Customers";
import Enquiries from "@/pages/Enquiries";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Agents from "@/pages/Agents";
import Products from "@/pages/Products";
import DeptQueue from "@/pages/DeptQueue";
import PublicEnquiry from "@/pages/PublicEnquiry";
import PublicApproval from "@/pages/PublicApproval";
import { AgentLogin, AgentRegister } from "@/pages/agent/AgentAuth";
import AgentLayout from "@/pages/agent/AgentLayout";
import AgentJobs from "@/pages/agent/AgentJobs";
import AgentNewOrder from "@/pages/agent/AgentNewOrder";
import AgentJobDetail from "@/pages/agent/AgentJobDetail";
import AgentProfile from "@/pages/agent/AgentProfile";
import "@/App.css";

function Protected({ children, perm }) {
  const { user, loading, hasPerm } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`} replace />;
  if (user.role === "agent") return <Navigate to="/agent" replace />;
  if (perm && !hasPerm(perm)) return <Navigate to="/" replace />;
  return children;
}

function Home() {
  const { hasPerm } = useAuth();
  const order = ["dashboard", "queue", "jobs", "production", "enquiries", "customers", "products", "agents", "settings", "reports", "system"];
  const paths = { system: "/system", dashboard: "/dashboard", queue: "/queue", jobs: "/jobs", production: "/production", customers: "/customers", enquiries: "/enquiries", products: "/products", agents: "/agents", reports: "/reports", settings: "/settings" };
  const first = order.find((m) => hasPerm(m));
  if (!first) return <div className="text-muted-foreground" data-testid="no-access">No modules assigned. Contact your admin.</div>;
  return <Navigate to={paths[first]} replace />;
}

const P = ({ perm, children }) => <Protected perm={perm}>{children}</Protected>;

function AgentProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/agent/login" replace />;
  if (user.role !== "agent") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/enquiry" element={<PublicEnquiry />} />
            <Route path="/approve/:token" element={<PublicApproval />} />
            <Route path="/agent/login" element={<AgentLogin />} />
            <Route path="/agent/register" element={<AgentRegister />} />
            <Route path="/agent" element={<AgentProtected><AgentLayout /></AgentProtected>}>
              <Route index element={<AgentJobs />} />
              <Route path="new" element={<AgentNewOrder />} />
              <Route path="jobs/:id" element={<AgentJobDetail />} />
              <Route path="profile" element={<AgentProfile />} />
            </Route>
            <Route path="/jobs/:id/print" element={<Protected><JobSheet /></Protected>} />
            <Route path="/labels" element={<Protected><LabelPrint /></Protected>} />
            <Route path="/scan/:id" element={<ScanJob />} />
            <Route path="/scan" element={<Protected><BulkScan /></Protected>} />
            <Route path="/stock/scan/:id" element={<Protected><StockScan /></Protected>} />
            <Route path="/stock/labels" element={<Protected><StockLabels /></Protected>} />
            <Route path="/stock/bulk" element={<Protected><StockBulkScan /></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Home />} />
              <Route path="/dashboard" element={<P perm="dashboard"><Dashboard /></P>} />
              <Route path="/jobs" element={<P perm="jobs"><Jobs /></P>} />
              <Route path="/queue" element={<P perm="queue"><DeptQueue /></P>} />
              <Route path="/jobs/new" element={<P perm="sales"><NewOrder /></P>} />
              <Route path="/jobs/:id" element={<JobDetail />} />
              <Route path="/production" element={<P perm="production"><ProductionBoard /></P>} />
              <Route path="/customers" element={<P perm="customers"><Customers /></P>} />
              <Route path="/enquiries" element={<P perm="enquiries"><Enquiries /></P>} />
              <Route path="/reports" element={<P perm="reports"><Reports /></P>} />
              <Route path="/settings" element={<P perm="settings"><Settings /></P>} />
              <Route path="/agents" element={<P perm="agents"><Agents /></P>} />
              <Route path="/stock" element={<P perm="stock"><Stock /></P>} />
              <Route path="/outsource" element={<P perm="stock"><OutsourceOrders /></P>} />
              <Route path="/attendance" element={<Attendance />} />
              <Route path="/awards" element={<Awards />} />
              <Route path="/products" element={<P perm="products"><Products /></P>} />
              <Route path="/system" element={<P perm="system"><SystemSettings /></P>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
